import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FlexRowBetween,
  GenericDiv,
  Input,
  Label,
  PopupForm,
  TextArea,
} from "../../common/Style";
import { useForm } from "react-hook-form";
import { CFV } from "../../common/others/Functions";
import OpacityLoading from "../../common/loader/OpacityLoading";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { deleteF, edit } from "../../../store/reducers/todosReducer";
import GCalendar from "../../common/calendar/Calendar";
import GenericModal from "../../common/modals/GenericModal";
import trash from "../../../assets/common/trash.svg";
import YesNoModal from "../../common/modals/YesNoModal";
import {
  CALL_TASK_BASE_LABELS,
  CALL_TASK_IDS,
  CALL_TIME_PRIORITY_OPTIONS,
  ROLE_6_MESSAGE_TYPES,
  SCHEDULE_MESSAGE_LABELS,
  buildCallTaskTitle,
  getAllowedLabelsForRole5Deal,
  getCallTaskBaseTitle,
  getExistingLabelsInSchedule,
  parseCallTaskPriorityIdFromTitle,
  validateRole6ReminderAndThankDate,
  validateRole6TaskOrder,
} from "./Add";

const MSG_SCHEDULED_NO_EDIT_DATE =
  "לא ניתן לערוך משימה מתוזמנת. יש למחוק את התיזמון ורק אז לערוך את המשימה.";
const MSG_SCHEDULED_NO_DELETE =
  "לא ניתן למחוק משימה מתוזמנת. יש לבטל קודם את התזמון ורק אז למחוק את המשימה.";

const Edit = ({ todo, init, userRole, isTal, eventDate, userDeal }) => {
  const dispatch = useDispatch();
  const { todos, todosLoading } = useSelector((state) => state.todosReducer);
  const isScheduled = !!(
    todo.ScheduledRequestUid != null && todo.ScheduledRequestUid !== ""
  );
  const isRole6 = userRole === 6 || (userRole === 1 && isTal);
  const role5PackageAllowed =
    userRole === 5 ? getAllowedLabelsForRole5Deal(userDeal) : null;
  const useStructuredSchedule = isRole6 || role5PackageAllowed != null;

  const defaultFormValues = useMemo(() => {
    const desc = todo.description || "";
    const pkg = userRole === 5 ? getAllowedLabelsForRole5Deal(userDeal) : null;
    const structured = isRole6 || pkg != null;
    const rawTitle = String(todo.title || "").trim();
    if (!structured) {
      const titleForSelect = CALL_TASK_BASE_LABELS.includes(
        getCallTaskBaseTitle(rawTitle),
      )
        ? getCallTaskBaseTitle(rawTitle)
        : rawTitle;
      return {
        title: titleForSelect,
        callTimePriority: parseCallTaskPriorityIdFromTitle(rawTitle),
        description: desc,
        talCustomTitle: isTal ? titleForSelect : "",
      };
    }
    let match = ROLE_6_MESSAGE_TYPES.find((t) => t.label === rawTitle);
    if (!match) {
      const base = getCallTaskBaseTitle(rawTitle);
      match = ROLE_6_MESSAGE_TYPES.find((t) => t.label === base);
    }
    const first =
      ROLE_6_MESSAGE_TYPES.find((t) => pkg == null || pkg.has(t.label)) ??
      ROLE_6_MESSAGE_TYPES[0];
    return {
      title: match?.label ?? first.label,
      messageTypeId: match?.id ?? first.id,
      callTimePriority:
        match && CALL_TASK_IDS.has(match.id)
          ? parseCallTaskPriorityIdFromTitle(rawTitle)
          : "free",
      description: desc,
      talCustomTitle: isTal ? match?.label ?? first.label : "",
    };
  }, [todo.title, todo.description, userRole, userDeal, isRole6, isTal]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: defaultFormValues,
  });

  const [showCalendar, setShowCalendar] = useState(null);
  const [talCustomTitleEnabled, setTalCustomTitleEnabled] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [showScheduledBlockModal, setShowScheduledBlockModal] = useState(false);
  const [scheduledBlockMessage, setScheduledBlockMessage] = useState("");

  const [date, setDate] = useState(new Date(todo.executionDate));
  const existingLabelsInSchedule = getExistingLabelsInSchedule(todos, todo._id);
  const existingTitleBases = useMemo(
    () =>
      new Set(
        [...existingLabelsInSchedule].map((x) => getCallTaskBaseTitle(x)),
      ),
    [existingLabelsInSchedule],
  );

  const selectedMessageTypeId = useStructuredSchedule
    ? watch("messageTypeId")
    : "";
  const selectedPlainTitle = !useStructuredSchedule ? watch("title") : "";
  const talCustomTitleWatch = isTal ? watch("talCustomTitle") : "";
  const talTrimmedWatch = isTal
    ? String(talCustomTitleWatch || "").trim()
    : "";
  const talCustomBaseForPriority =
    isTal && talTrimmedWatch
      ? getCallTaskBaseTitle(talTrimmedWatch)
      : "";
  const showCallTimePriority =
    (useStructuredSchedule &&
      ((selectedMessageTypeId &&
        CALL_TASK_IDS.has(selectedMessageTypeId)) ||
        (talTrimmedWatch &&
          CALL_TASK_BASE_LABELS.includes(talCustomBaseForPriority)))) ||
    (!useStructuredSchedule &&
      (CALL_TASK_BASE_LABELS.includes(String(selectedPlainTitle || "").trim()) ||
        (talTrimmedWatch &&
          CALL_TASK_BASE_LABELS.includes(talCustomBaseForPriority))));

  useEffect(() => {
    if (!isTal || talCustomTitleEnabled) return;
    const label = useStructuredSchedule
      ? ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)?.label ??
        ""
      : String(selectedPlainTitle || "").trim();
    setValue("talCustomTitle", label, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [
    isTal,
    talCustomTitleEnabled,
    selectedMessageTypeId,
    selectedPlainTitle,
    useStructuredSchedule,
    setValue,
  ]);

  const handleTalTypeLabelContextMenu = (e) => {
    if (!isTal) return;
    e.preventDefault();
    setTalCustomTitleEnabled((prev) => {
      if (prev) {
        const label = useStructuredSchedule
          ? ROLE_6_MESSAGE_TYPES.find((t) => t.id === selectedMessageTypeId)
              ?.label ?? ""
          : String(selectedPlainTitle || "").trim();
        setValue("talCustomTitle", label, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      return !prev;
    });
  };

  const handleSetDate = (e) => {
    let date1 = e;
    date1.setHours(20);
    setDate(date1);
  };

  const handleDateFieldClick = () => {
    if (isScheduled) {
      setScheduledBlockMessage(MSG_SCHEDULED_NO_EDIT_DATE);
      setShowScheduledBlockModal(true);
      return;
    }
    setShowCalendar(true);
  };

  const onSubmit = async (data) => {
    if (isScheduled) {
      const originalDateStr = new Date(todo.executionDate)
        .toISOString()
        .slice(0, 10);
      const newDateStr = date.toISOString().slice(0, 10);
      if (originalDateStr !== newDateStr) {
        toast.warning(MSG_SCHEDULED_NO_EDIT_DATE);
        return;
      }
    }
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      toast.warning("לא ניתן לבחור תאריך עבר למשימה");
      return;
    }

    let titleForBody = "";
    const rawTal = String(
      data.talCustomTitle ?? getValues("talCustomTitle") ?? "",
    ).trim();
    const talTrim =
      isTal && talCustomTitleEnabled && rawTal ? rawTal : "";

    const applyCallPriorityIfCallTask = (t) => {
      const base = getCallTaskBaseTitle(t);
      if (CALL_TASK_BASE_LABELS.includes(base)) {
        return buildCallTaskTitle(base, data.callTimePriority || "free");
      }
      return t;
    };

    if (useStructuredSchedule) {
      if (talTrim) {
        titleForBody = applyCallPriorityIfCallTask(talTrim);
      } else {
        const baseTitle = String(
          ROLE_6_MESSAGE_TYPES.find((t) => t.id === data.messageTypeId)
            ?.label ?? "",
        ).trim();
        if (!baseTitle) {
          toast.warning("יש לבחור סוג הודעה מהרשימה");
          return;
        }
        titleForBody = CALL_TASK_IDS.has(data.messageTypeId)
          ? buildCallTaskTitle(baseTitle, data.callTimePriority || "free")
          : baseTitle;
      }
      const listLabels = new Set(ROLE_6_MESSAGE_TYPES.map((t) => t.label));
      const titleBase = getCallTaskBaseTitle(titleForBody);
      const packageKey = listLabels.has(titleForBody)
        ? titleForBody
        : listLabels.has(titleBase)
          ? titleBase
          : null;
      if (
        role5PackageAllowed != null &&
        packageKey != null &&
        !role5PackageAllowed.has(packageKey)
      ) {
        toast.warning("סוג משימה זה לא כלול בחבילה שלך");
        return;
      }
      if (packageKey != null && existingTitleBases.has(packageKey)) {
        toast.warning(`משימה מסוג "${packageKey}" כבר קיימת בלו"ז`);
        return;
      }
      const dateCheck = validateRole6ReminderAndThankDate(
        eventDate,
        date.toISOString(),
        titleForBody,
      );
      if (!dateCheck.valid) {
        toast.warning(dateCheck.message);
        return;
      }
      const orderCheck = validateRole6TaskOrder(
        todos,
        date.toISOString(),
        titleForBody,
        todo._id,
      );
      if (!orderCheck.valid) {
        toast.warning(orderCheck.message);
        return;
      }
    } else {
      let plainTitle;
      if (talTrim) {
        plainTitle = applyCallPriorityIfCallTask(talTrim);
      } else {
        plainTitle = String(data.title || "").trim();
        if (!plainTitle) {
          toast.warning("יש לבחור סוג משימה מהרשימה");
          return;
        }
      }
      const skipStructuredValidation =
        userRole === 5 && getAllowedLabelsForRole5Deal(userDeal) == null;
      const titleBaseForRules = getCallTaskBaseTitle(plainTitle);
      let finalPlainTitle = plainTitle;
      if (
        !skipStructuredValidation &&
        SCHEDULE_MESSAGE_LABELS.has(titleBaseForRules)
      ) {
        const existingOther = getExistingLabelsInSchedule(todos, todo._id);
        const existingBases = new Set(
          [...existingOther].map((x) => getCallTaskBaseTitle(x)),
        );
        if (existingBases.has(titleBaseForRules)) {
          toast.warning(`משימה מסוג "${titleBaseForRules}" כבר קיימת בלו"ז`);
          return;
        }
        if (CALL_TASK_BASE_LABELS.includes(plainTitle)) {
          finalPlainTitle = buildCallTaskTitle(
            plainTitle,
            data.callTimePriority || "free",
          );
        }
        const dateCheckOther = validateRole6ReminderAndThankDate(
          eventDate,
          date.toISOString(),
          finalPlainTitle,
        );
        if (!dateCheckOther.valid) {
          toast.warning(dateCheckOther.message);
          return;
        }
        const orderCheckOther = validateRole6TaskOrder(
          todos,
          date.toISOString(),
          finalPlainTitle,
          todo._id,
        );
        if (!orderCheckOther.valid) {
          toast.warning(orderCheckOther.message);
          return;
        }
      } else if (CALL_TASK_BASE_LABELS.includes(plainTitle)) {
        finalPlainTitle = buildCallTaskTitle(
          plainTitle,
          data.callTimePriority || "free",
        );
      }
      titleForBody = finalPlainTitle;
    }
    const body = {
      ...data,
      title: titleForBody,
      executionDate: date.toISOString(),
    };
    if (useStructuredSchedule) delete body.messageTypeId;
    delete body.callTimePriority;
    delete body.talCustomTitle;
    dispatch(edit(todo._id, body)).then((res) => {
      if (!res.payload?.error) {
        toast.success("הפעולה בוצעה בהצלחה");
        if (isTal) setTalCustomTitleEnabled(false);
        init();
      } else toast.error(res.payload?.error);
    });
  };

  const handleDeleteClick = () => {
    if (isScheduled) {
      setScheduledBlockMessage(MSG_SCHEDULED_NO_DELETE);
      setShowScheduledBlockModal(true);
      return;
    }
    setShowDelete(true);
  };

  const handleDelete = () => {
    dispatch(deleteF(todo._id)).then((res) => {
      if (!res.payload?.error) {
        toast.success("הפעולה בוצעה בהצלחה");
        init();
      } else toast.error(res.payload?.error);
    });
  };

  return (
    <PopupForm
      onClick={(e) => e.stopPropagation()}
      onSubmit={handleSubmit(onSubmit)}
      className="animate__animated animate__fadeIn"
    >
      <div
        style={{
          width: "90%",
          margin: "15px auto",
        }}
      >
        {useStructuredSchedule ? (
          <>
            <div
              style={{
                fontSize: "14px",
                lineHeight: 1.5,
                color: "#37474f",
                marginBottom: "16px",
                textAlign: "right",
                direction: "rtl",
                padding: "10px 12px",
                background: "#eceff1",
                borderRadius: "8px",
              }}
            >
              נוסחי ההודעות מופיעים במסמך תיאום הציפיות שבעמוד ההדרכות.
            </div>
            {/*
              בעבר: isTal — שדה כותרת חופשית + select "בחירה מהירה" (value/onChange + setValue ל-title).
              בעבר: ללא isTal — select עם value={selectedPresetId} ו-onChange.
              כרגע: כמו Add.js — רק register(messageTypeId). להחזיר: ראה הערה מקבילה ב-Add.js.
            */}
            <Label
              onContextMenu={isTal ? handleTalTypeLabelContextMenu : undefined}
            >
              סוג ההודעה
            </Label>
            <select
              {...register("messageTypeId", { required: true })}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#fff",
                marginBottom: "8px",
              }}
            >
              {ROLE_6_MESSAGE_TYPES.map((type) => {
                const blockedByPackage =
                  role5PackageAllowed != null &&
                  !role5PackageAllowed.has(type.label);
                const disabled =
                  existingTitleBases.has(type.label) || blockedByPackage;
                return (
                  <option key={type.id} value={type.id} disabled={disabled}>
                    {type.label}
                    {existingTitleBases.has(type.label)
                      ? ' (קיים בלו"ז)'
                      : blockedByPackage
                        ? " (לא כלול בחבילה)"
                        : ""}
                  </option>
                );
              })}
            </select>
            {CFV("messageTypeId", "יש לבחור סוג הודעה", errors)}
            {isTal ? (
              <div style={{ marginTop: "12px" }}>
                <Label style={{ display: "block" }}>כותרת חופשית (מנהל)</Label>
                <Input
                  type="text"
                  autoComplete="off"
                  {...register("talCustomTitle", {
                    maxLength: 50,
                    validate: (v) => {
                      const s = String(v || "").trim();
                      if (!s) return true;
                      if (s.length < 2) return "בין 2 ל־50 תווים";
                      return true;
                    },
                  })}
                  disabled={!talCustomTitleEnabled}
                />
                {CFV("talCustomTitle", "בין 2 ל־50 תווים", errors)}
              </div>
            ) : null}
            {showCallTimePriority && useStructuredSchedule ? (
              <>
                <Label style={{ marginTop: "12px", display: "block" }}>
                  עדיפות לביצוע בשעות היום
                </Label>
                <select
                  {...register("callTimePriority")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {CALL_TIME_PRIORITY_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </>
        ) : (
          <>
            {/*
              בעבר: שדה כותרת חופשית (Input). להחזיר: שחרר את הבלוק המוערת — מקביל ל-Add.js.
              <Label>כותרת</Label>
              <Input
                type="text"
                {...register("title", {
                  required: true,
                  minLength: 2,
                  maxLength: 50,
                })}
              />
            */}
            <Label
              onContextMenu={isTal ? handleTalTypeLabelContextMenu : undefined}
            >
              סוג משימה
            </Label>
            <select
              {...register("title", { required: true })}
              style={{
                width: "100%",
                padding: "10px 12px",
                fontSize: "16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                backgroundColor: "#fff",
                marginBottom: "8px",
              }}
            >
              <option value="">— בחר סוג משימה —</option>
              {ROLE_6_MESSAGE_TYPES.map((type) => {
                const dupBlocked =
                  SCHEDULE_MESSAGE_LABELS.has(type.label) &&
                  existingTitleBases.has(type.label);
                return (
                  <option
                    key={type.id}
                    value={type.label}
                    disabled={dupBlocked}
                  >
                    {type.label}
                    {dupBlocked ? ' (קיים בלו"ז)' : ""}
                  </option>
                );
              })}
            </select>
            {CFV("title", "יש לבחור סוג משימה", errors)}
            {isTal ? (
              <div style={{ marginTop: "12px" }}>
                <Label style={{ display: "block" }}>כותרת חופשית (מנהל)</Label>
                <Input
                  type="text"
                  autoComplete="off"
                  {...register("talCustomTitle", {
                    maxLength: 50,
                    validate: (v) => {
                      const s = String(v || "").trim();
                      if (!s) return true;
                      if (s.length < 2) return "בין 2 ל־50 תווים";
                      return true;
                    },
                  })}
                  disabled={!talCustomTitleEnabled}
                />
                {CFV("talCustomTitle", "בין 2 ל־50 תווים", errors)}
              </div>
            ) : null}
            {showCallTimePriority && !useStructuredSchedule ? (
              <>
                <Label style={{ marginTop: "12px", display: "block" }}>
                  עדיפות לביצוע בשעות היום
                </Label>
                <select
                  {...register("callTimePriority")}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "16px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    marginBottom: "8px",
                  }}
                >
                  {CALL_TIME_PRIORITY_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </>
        )}

        {role5PackageAllowed == null ? (
          <>
            <Label>הערות</Label>
            <TextArea
              defaultValue={todo.description}
              type="text"
              rows="3"
              {...register("description", {
                minLength: 0,
                maxLength: 500,
              })}
            />
            {CFV("description", "עד 500 תווים", errors)}
          </>
        ) : null}

        <Label style={{ marginTop: "10px" }}>תאריך לביצוע</Label>
        <Input
          onClick={handleDateFieldClick}
          value={date
            .toISOString()
            .replace(/T.*/, "")
            .split("-")
            .reverse()
            .join("/")}
          onChange={() => {}}
          type={"text"}
        />
        <FlexRowBetween style={{ marginTop: "50px" }}>
          <Button style={{ width: "100%" }}>שמירה</Button>
          <GenericDiv
            onClick={handleDeleteClick}
            background={"rgba(41,59,90,0.95)"}
            style={{ marginRight: "20px", padding: "9px 10px" }}
          >
            <img src={trash} alt="trash" style={{ width: "21px" }} />
          </GenericDiv>
        </FlexRowBetween>
      </div>

      {todosLoading && <OpacityLoading />}

      {showCalendar && (
        <GenericModal
          init={setShowCalendar}
          div={
            <GCalendar
              setDate={handleSetDate}
              date={date}
              init={setShowCalendar}
            />
          }
        />
      )}

      {showDelete && (
        <GenericModal
          init={setShowDelete}
          div={
            <YesNoModal
              title={"מחיקת משימה"}
              body={"האם את/ה בטוח/ה שברצונך למחוק משימה זו?"}
              no={setShowDelete}
              yes={handleDelete}
            />
          }
        />
      )}

      {showScheduledBlockModal && (
        <GenericModal
          init={() => setShowScheduledBlockModal(false)}
          div={
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                padding: "24px",
                minWidth: "280px",
                background: "#fff",
                borderRadius: "12px",
                border: "2px solid #7b96c4",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  marginBottom: "20px",
                  fontSize: "16px",
                  lineHeight: 1.5,
                }}
              >
                {scheduledBlockMessage}
              </p>
              <Button onClick={() => setShowScheduledBlockModal(false)}>
                הבנתי
              </Button>
            </div>
          }
        />
      )}
    </PopupForm>
  );
};

export default Edit;
